import { PDFCustomDataKey, Queries } from '@aline/common';
import type { Core } from '@pdftron/webviewer';

import type { FocusedField } from '../variables/document-variables/variable-panel-state-store';

/**
 * Whether a PDF widget belongs to the field currently focused in the Outline
 * panel. Drives both the focused styling in `getContainerCustomStyles` and the
 * redraw in `useRegisterFocusedAnnotationListener`, so the two always agree on
 * which widgets are lit up.
 *
 * Takes only the `getCustomData` reader rather than a whole annotation, so the
 * matching rules can be exercised without a live WebViewer.
 */
export function isPdfWidgetFocused(
  widget: Pick<Core.Annotations.Annotation, 'getCustomData'>,
  focusedField: FocusedField | null
): boolean {
  if (!focusedField) return false;

  /**
   * A canned widget carries the CannedField's `nodeId` in `cannedFieldId`
   * custom data, which is what `focusedField.nodeId` mirrors both from a
   * caret focus and from an Outline row click. Matching there highlights
   * every placement of the focused canned field, the same way a regular
   * field's widgets all highlight together.
   *
   * Canned and non-canned are matched exclusively rather than falling
   * through to the `nodeId` comparison below, where the two id spaces
   * could otherwise collide.
   */
  const cannedFieldId = widget.getCustomData(PDFCustomDataKey.cannedFieldId);
  if (focusedField.__typename === 'CannedField')
    return Boolean(cannedFieldId) && cannedFieldId === focusedField.nodeId;
  if (cannedFieldId) return false;

  const widgetNodeId = widget.getCustomData(PDFCustomDataKey.nodeId);
  if (!widgetNodeId) return false;
  if (focusedField.nodeId === widgetNodeId) return true;

  // A checkbox option widget is keyed by a composite id; it belongs to the
  // variable named in the first half.
  const parsed = Queries.parseCheckboxOptionNodeId(widgetNodeId);
  return parsed?.variableNodeId === focusedField.nodeId;
}
